import { IconArrowDown, IconArrowUp } from '@tabler/icons-react'
import { NO_CHANGE, formatDayCount } from '../format'

/**
 * A change against the comparison period: counts in percent, times to resolve in whole days,
 * with an arrow. Coloured by whether the move is good for this metric (`better`): a falling
 * count of open gaps is good, a rising one is not. No change or "—" stays neutral.
 */
export function ChangeText({
  value,
  unit,
  better = 'up',
  label = 'vs prior',
}: {
  value: number | null
  unit: 'percent' | 'days'
  /** Which direction is an improvement for this metric. */
  better?: 'up' | 'down'
  label?: string
}) {
  if (value === null) {
    return (
      <span className="text-xs whitespace-nowrap text-slate-400">
        <span className="font-medium">{NO_CHANGE}</span>
        {label && <span> {label}</span>}
      </span>
    )
  }
  // Days show whole: a change that rounds to 0 days reads as unchanged, not as a tiny move.
  const shown = unit === 'days' ? Math.round(value) : value
  const magnitude = unit === 'percent' ? `${Math.abs(shown)}%` : formatDayCount(Math.abs(shown))
  const good = shown > 0 === (better === 'up')
  const tone = shown === 0 ? 'text-slate-500' : good ? 'text-emerald-600' : 'text-red-600'
  const Arrow = shown > 0 ? IconArrowUp : IconArrowDown
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs whitespace-nowrap ${tone}`}>
      {shown !== 0 && <Arrow size={12} stroke={2.5} aria-hidden />}
      <span className="sr-only">{shown > 0 ? 'up' : shown < 0 ? 'down' : 'unchanged'} </span>
      <span className="font-medium tabular-nums">{magnitude}</span>
      {label && <span className="text-slate-500"> {label}</span>}
    </span>
  )
}
